import { useState } from 'react'
import axios from 'axios'
import type { UploadRequestOption } from 'rc-upload/lib/interface'
import { apiCall } from '../../../api/apiCall'
import { AttachmentRecord, UploadingFile, UseFileUploadOptions } from './types'
import { MESSAGES } from './constants'
import { snakeCaseToCamelCase } from '../../../utils/snakeCaseToCamelCase'
import { extractS3Key } from './helpers'

export const useFileUpload = ({ presignedPostPath, confirmPath, onUploadComplete }: UseFileUploadOptions) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const [uploadErrors, setUploadErrors] = useState<string[]>([])

  const handleUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError, onProgress } = options
    const rcFile = file as File & { uid: string }

    setUploadingFiles((prev) => [...prev, { uid: rcFile.uid, name: rcFile.name, percent: 0 }])

    const updatePercent = (percent: number) => {
      setUploadingFiles((prev) => prev.map((f) => (f.uid === rcFile.uid ? { ...f, percent } : f)))
      onProgress?.({ percent })
    }

    const removeFromUploading = () => setUploadingFiles((prev) => prev.filter((f) => f.uid !== rcFile.uid))

    try {
      const presignedResponse = await apiCall.post(presignedPostPath, {})
      const { url, fields } = presignedResponse.data

      const formData = new FormData()
      Object.keys(fields as Record<string, string>).forEach((key) => {
        formData.append(key, (fields as Record<string, string>)[key])
      })
      formData.append('Content-Type', rcFile.type || 'binary/octet-stream')
      formData.append('file', rcFile)

      const s3Response = await axios.post(url, formData, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 90) / progressEvent.total)
            updatePercent(percent)
          }
        }
      })

      const s3Key = extractS3Key(s3Response.data, fields.key)

      updatePercent(95)

      const confirmResponse = await apiCall.post(confirmPath, {
        file: {
          key: s3Key,
          content_type: rcFile.type,
          file_size: rcFile.size
        }
      })

      updatePercent(100)
      onSuccess?.(confirmResponse.data)
      removeFromUploading()
      setUploadErrors([])
      onUploadComplete?.(snakeCaseToCamelCase(confirmResponse.data.attachment) as AttachmentRecord)
    } catch (err: any) {
      const errors = err?.response?.data?.errors || [MESSAGES.UPLOAD_ERROR_FALLBACK]
      onError?.(err)
      removeFromUploading()
      setUploadErrors(errors)
    }
  }

  return { uploadingFiles, uploadErrors, handleUpload }
}
